//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { LocalDictionaryFactory } from '../../../api/api.module'
import { ColumnModel } from '../../../api/api.models'

import { CommonHelper } from '../../../../helpers/common-helper'
import { EventsNames } from '../../../../helpers/events-names'
import { OperatorLayoutSettings } from '../../../../helpers/operator-layout-settings'

import { Activity, Enums } from '../../../../models/common'
import { OpOperator, OpRoute, OpDataTypeInGroup, OpTask, OpFile, OpView } from '../../../../models/core'
import { OpPolygon, OpRange, OpMarker, OpMapArea } from '../../../../models/custom/op-map-area'

import { TypeHelper, TemplateHelper, ConfigHelper, ConvertHelper, LogHelper, ToastHelper } from '../../../../helpers'
import { CancellationTokenSource } from '../../../../helpers/cancellation-token'
import { LocalizationHelper } from '../../../../helpers/localization-helper'
import { MapHelper } from '../../../../helpers/map-helper'

import { DetailsOptions, SystemService } from '../../../../services/system'
import { RouteService, RoutePointService, RouteOperatorService, RouteFitterService } from '../../../../services/route'
import { UserSessionService } from '../../../../services/user-session'

import { Details, DetailsScope, DetailsAccordion, DetailsAccordionSection } from '../../../common/common.details'
import { DetailsParametersOptions } from '../../../common/details-parameters/details-parameters.directive'
import { DetailsPopupOptions } from '../../../common/details-popup/details-popup.directive'
import { GridBridge } from '../../../common/common.interfaces'
import { GridOptions, GridTypes, GridController } from '../../../common/grid'
import { IMRConfirmationPopupOptions, IIMRConfirmationPopup, IMRConfirmationPopupInitializedArgs } from '../../../common/confirmation-popup2'
import { MapOptions, Map, MapButtonClass, MapLegendItem } from '../../../common/map2/map.directive.controller'
import { RouteTrackingOptions } from '../tracking'


interface RouteDetailsScope extends DetailsScope {
    id: number
}

export class RouteDetailsDirectiveController extends Details<RouteDetailsScope> {

    //#region Consts
    private static get LOG_PREFIX(): string { return '[SIMAX.RouteDetailsDirectiveController]' }
    public get ROUTE_COMMON_TAB_NAME() { return 'ROUTE_COMMON_TAB_NAME' }
    public get ROUTE_MAP_ACCORDION_NAME() { return 'ROUTE_MAP_ACCORDION_NAME' }
    public get ROUTE_OPERATORS_ACCORDION_NAME() { return 'ROUTE_OPERATORS_ACCORDION_NAME' }
    public get ROUTE_OPERATORS_GRID_EDIT_NAME() { return 'routeOperatorsGrid' }
    public get ROUTE_OPERATORS_GRID_EDIT_POPUP_ID() { return this.$scope.detailsPopupId + '_ROUTE_OPERATORS_GRID_EDIT_ID' }
    public get ROUTE_OPERATORS_GRID_NAME() { return 'RoutesPointsDetails' }
    public get ROUTE_PARAMS_ACCORDION_NAME() { return 'ROUTE_PARAMS_ACCORDION_NAME' }
    public get ROUTE_POINTS_ACCORDION_NAME() { return 'ROUTE_POINTS_ACCORDION_NAME' }
    public get ROUTE_POINTS_GRID_EDIT_NAME() { return 'routePointsGrid' }
    public get ROUTE_POINTS_GRID_EDIT_POPUP_ID() { return this.$scope.detailsPopupId + '_ROUTE_POINTS_GRID_EDIT_ID' }
    public get ROUTE_POINTS_GRID_NAME() { return 'RoutesPointsDetails' }
    public get ROUTE_TRACKS_TAB_NAME() { return 'ROUTE_TRACKS_TAB_NAME' }

    //#endregion


    //#region Members

    private idRoute: number
    private route: OpRoute

    //#endregion


    //#region Ctor

    constructor(
        $scope: RouteDetailsScope,
        $q: ng.IQService,
        $timeout: ng.ITimeoutService,
        localDictionary: LocalDictionaryFactory,
        private routeService: RouteService,
        private routeFitterService: RouteFitterService,
        private routeOperatorService: RouteOperatorService,
        private routePointService: RoutePointService,
        private systemService: SystemService,
        protected userSessionService: UserSessionService,
        protected referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super(
            $scope,
            $timeout,
            $q, null,
            localDictionary,
            OperatorLayoutSettings.RoutePopupTabActive,
            userSessionService,
            referenceType
        )

        this.initializeConfirmationPopup()
        this.setOptions()
        this.setEvents()

        if (this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_LIST)) {
            this.configureTabs(this.getTabs())
        }
    }

    //#endregion


    //#region Events

    private onClear(): void {
        this.$scope.popupTitle = TypeHelper.emptyString
        this.$scope.showTabs = false

        this.route = null
        this.idRoute = null

        this.clearPoints()
        this.clearMap()
        this.clearOperators()
        this.clearFitter()
        this.cleanTracking()
    }

    private onClose(): void {
        this.pointsGrid = null
        this.operatorsGrid = null

        this.hidePopup()
    }

    private onConfirmDelete(): void {
        this.routeService
            .deleteRoute(this.idRoute)
            .then(() => {
                this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName))
                this.onClose()
            })
    }

    private onDelete(): void {
        this.confirmationPopup.show({
            content: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName),
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),

            onConfirmed: () => {
                this.onConfirmDelete()
            }
        })
    }

    private onEditParams(): void {
        this.systemService.openCreateEdit(this.routeService.getCreateEditOptions(this.route.idRoute, this.route))
    }

    private onEditOperators(): void {
        this.$scope.$broadcast(EventsNames.OPEN_GRID_EDIT_POPUP + this.ROUTE_OPERATORS_GRID_EDIT_POPUP_ID)
    }

    private onEditPoints(): void {
        this.$scope.$broadcast(EventsNames.OPEN_GRID_EDIT_POPUP + this.ROUTE_POINTS_GRID_EDIT_POPUP_ID, "routePoints")
    }

    private onOpen(options: DetailsOptions): void {
        this.onClear()

        this.$scope.detailsOpenParams = options

        this.idRoute = options.referenceObjectId

        this.showPopup()
        this.onRefresh()
    }

    private onRefresh(toRefresh: OpRoute = null): void {
        if (!toRefresh) {
            this.routeService
                .getRoute(this.idRoute)
                .then((route: OpRoute) => {
                    this.initialize(route)
                })
                .fail(() => {
                    this.hidePopup()
                })
        } else {
            this.initialize(toRefresh)
        }
    }

    //#endregion


    //#region Initialize

    private initialize(route: OpRoute): void {
        if (route != null && (!this.route || this.route.idRoute == route.idRoute)) {
            this.ensureOperatorExecutorExists(this.route, route)

            this.route = route
            this.$scope.popupTitle = route.toString()

            this.initializeMap()
            this.initializeBasicParameters(route)
            this.initializePoints(route)
            this.initializeOperators(route)
            this.initializeFitterOperator(route)
            this.initializeTracking(route)

            this.$scope.showTabs = true
        }
    }

    /**
     * OperatorExecutor is not returned from OpxSaveRoute, so I'm copying it from the existing route.
     */
    private ensureOperatorExecutorExists(existingRoute: OpRoute, newRoute: OpRoute) {
        if (existingRoute != null && newRoute != null
            && existingRoute.idOperatorExecutor === newRoute.idOperatorExecutor
            && newRoute.operatorExecutor == null
            && existingRoute.operatorExecutor != null
        ) {
            newRoute.operatorExecutor = existingRoute.operatorExecutor.clone(true, true, true, true)
        }
    }

    //#endregion


    //#region Parameters

    public basicParametersOptions: DetailsParametersOptions

    private initializeBasicParameters(route: OpRoute): void {
        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.RouteParameters)) {
            this.routeService
                .getBasicParametersDataTypeGroup()
                .then((dataTypeInGroup: OpDataTypeInGroup[]) => {
                    this.basicParametersOptions = {
                        dataTypes: dataTypeInGroup,
                        referenceType: this.referenceType,
                        referenceObject: route,
                        useDynamicValues: true
                    }
                })
        }
    }

    //#endregion


    //#region Points

    public pointsGridOptions: GridOptions
    public pointsGridEditOptions: GridBridge

    private pointsGrid: GridController<OpTask>
    private pointsGridEditController: any

    private clearPoints(): void {
        this.pointsGrid = null
        this.pointsGridOptions = null
        this.pointsGridEditController = null
    }

    private clearHiddenPoints(): void {
        this.pointsGrid = null
    }

    private initializePoints(route: OpRoute): void {
        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.RoutePoints)) {
            this.initializePointsGrid(route)
            this.initializePointsGridEdit(route)
        }
    }

    private initializePointsGrid(route: OpRoute): void {
        this.pointsGridOptions = null

        this.$timeout(() => {
            $
                .when<any>(
                    this.routePointService.getPointsExportWhereClause(route.idRoute),
                    this.routePointService.getPointsGridColumns(route)
                )
                .then((exportWhereClause: string, columns: ColumnModel[]) => {
                    this.pointsGridOptions = {
                        addCustomFilters: true,
                        columnsDictionary: columns,
                        dataSource: route.taskList,
                        idReferenceType: Enums.ReferenceType.IdTask,
                        name: this.ROUTE_POINTS_GRID_NAME,
                        type: GridTypes.PAGINATED_SMALL,

                        buttonsOptions: {
                            enabled: true,
                            exportPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.TASK_EXPORT_OP) ? "['" + Activity[Activity.TASK_EXPORT_OP] + "']" : null
                        },

                        exportOptions: {
                            referenceType: Enums.ReferenceType.IdTask,
                            customWhere: exportWhereClause,
                            defaultExportDLL: 'wRouteTaskExport',
                            pluginOptions: {
                                moduleName: Enums.SimaxModuleName.Task,
                                popupName: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__EXPORT, Enums.SimaxModuleName.Task),
                            },
                        },

                        allowColumnReordering: !this.userSession
                            .getLoggedOperator()
                            .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.RoutePoints),

                        onInitialized: (e: any, grid: GridController<OpTask>) => {
                            this.pointsGrid = grid
                        },

                        onSelectionChanged: () => {
                            this.updatePointSelection()
                        },

                        onRowDoubleClick: () => {
                            this.openTaskDetails(this.pointsGrid.selectedItemId, this.pointsGrid.pageItemsIds)
                        },
                    }
                })
        }, 100)
    }

    private initializePointsGridEdit(route: OpRoute): void {
        const cancellationTokenSource: CancellationTokenSource = new CancellationTokenSource()
        let isCloseConfirmed: boolean = false
        let isSaving: boolean = false

        $
            .when<any>(
                this.routePointService.getPointsEditGridColumns(route),
                this.routePointService.getLocationView()
            )
            .then((columns: ColumnModel[], routeView: OpView) => {
                this.$timeout(() => {
                    this.pointsGridEditOptions = {
                        columns: columns,
                        gridReferenceId: Enums.ReferenceType.IdLocation,
                        id: this.route.idRoute,
                        isLoadingIndicatorDisabled: true,
                        items: this.routePointService.getPointGridEditObjectsFromRoute(route),
                        name: this.ROUTE_POINTS_GRID_EDIT_NAME,
                        popupId: this.ROUTE_POINTS_GRID_EDIT_POPUP_ID,
                        title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.PointsEdit),

                        allowColumnReordering: !this.userSession
                            .getLoggedOperator()
                            .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.RoutePoints),

                        dataSourceParser: this.routePointService.getPointGridEditDataSourceParser(routeView),
                        getAvailableDataSourceMethod: this.userSessionService.getDataProvider().Location.getLocationFilter,

                        onControllerInitialized: (component: any) => {
                            this.pointsGridEditController = component
                        },

                        onHiding: (e: any) => {
                            // TODO dodać do imr-popup opcję onHidingAsync
                            if (!isCloseConfirmed && isSaving) {
                                e.cancel = true

                                this.confirmationPopup.show({
                                    content: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AreYouSure),
                                    title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SavingInProgress),

                                    onConfirmed: () => {
                                        isCloseConfirmed = true
                                        cancellationTokenSource.cancel()
                                        e.cancel = false
                                        e.component.hide()
                                    }
                                })
                            }
                        },

                        onSubmit: (added: any[], removed: any[], inGrid: any[]) => {
                            const deferred: ng.IDeferred<boolean> = this.$q.defer<boolean>()

                            if (_.some(inGrid, (item: any) => item.id == null || +item.id == NaN || !_.isNumber(item.id))) {
                                ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ErrorContactWithAdmin))
                                LogHelper.error(RouteDetailsDirectiveController.LOG_PREFIX, 'Error. Cant save route. Some task doesnt have assigned location')
                                deferred.reject()
                            } else {
                                const tasksToSave: OpTask[] = this.routePointService.getTasksFromGridEditObjects(inGrid)

                                isSaving = true
                                isCloseConfirmed = false

                                this.pointsGridEditController.setProgress(0)
                                this.pointsGridEditController.toggleProgressBar(this.routePointService.isPartSavingRequired(this.route, tasksToSave))
                                this.pointsGridEditController.setIsLoading(true)

                                this.routePointService
                                    .saveTasksToRoute(this.route, tasksToSave, cancellationTokenSource.token, (progress: number) => {
                                        this.pointsGridEditController.setProgress(progress)
                                    })
                                    .then((savedRoute: OpRoute) => {
                                        ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, RouteService.SIMAX_MODULE_NAME))

                                        this.onRefresh(savedRoute)

                                        deferred.resolve(true)
                                    })
                                    .fail((error: any) => {
                                        ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVE_ERROR, RouteService.SIMAX_MODULE_NAME))
                                        LogHelper.warn(RouteService.LOG_PREFIX, `Unable to save points for route with id ${route.idRoute}.`, error)

                                        deferred.resolve(false)
                                    })
                                    .always(() => {
                                        this.pointsGridEditController.setIsLoading(false)
                                        isSaving = false
                                    })

                            }

                            return deferred.promise
                        }
                    }
                }, 200)
            })
    }

    private openTaskDetails(idTask: number, pageTaskIds: number[]): void {
        this.routePointService.openTaskDetails(idTask, pageTaskIds)
    }

    private updatePointSelection(): void {
        if (!this.isMarkerClicked) {
            this.updateSelectMarkers()
        } else {
            if (this.pointsGrid != null) {
                this.pointsGrid.scrollToSelected()
            }
        }

        this.isMarkerClicked = false
    }

    //#endregion


    //#region Operators

    public operatorsGridOptions: GridOptions
    public operatorsGridEditOptions: GridBridge
    private operatorsGrid: GridController<OpOperator>

    private clearOperators(): void {
        this.operatorsGrid = null
        this.operatorsGridOptions = null
        this.operatorsGridEditOptions = null
    }

    private clearHiddenOperators(): void {
        this.operatorsGrid = null
    }

    private initializeOperators(route: OpRoute): void {
        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.RouteAssignedOperators)) {
            this.initializeOperatorsGrid(route)
            this.initializeOperatorsGridEdit(route)
        }
    }

    private initializeOperatorsGrid(route: OpRoute): void {
        this.operatorsGridOptions = null

        this.$timeout(() => {
            this.routeOperatorService
                .getOperatorGridColumns(route)
                .then((columns: ColumnModel[]) => {
                    this.operatorsGridOptions = {
                        addCustomFilters: true,
                        columnsDictionary: columns,
                        idReferenceType: Enums.ReferenceType.IdOperator,
                        name: this.ROUTE_OPERATORS_GRID_NAME,
                        type: GridTypes.NO_SEARCH_BAR_PAGINATED_SMALL,

                        buttonsOptions: {
                            enabled: true,
                            isRefreshButonHidden: true
                        },

                        allowColumnReordering: !this.userSession
                            .getLoggedOperator()
                            .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.RouteAssignedOperators),

                        getDataSource: () => {
                            return this.route.operatorList
                        },

                        onInitialized: (e: any, grid: GridController<OpOperator>) => {
                            this.operatorsGrid = grid
                        },

                        onRowDoubleClick: () => {
                            this.openOperatorDetails(this.operatorsGrid.selectedItemId, this.operatorsGrid.pageItemsIds)
                        },
                    }
                })
        }, 100)
    }

    private initializeOperatorsGridEdit(route: OpRoute): void {
        this.routeOperatorService
            .getOperatorGridColumns(route)
            .then((columns: ColumnModel[]) => {
                this.$timeout(() => {
                    this.operatorsGridEditOptions = {
                        columns: columns,
                        gridReferenceId: Enums.ReferenceType.IdOperator,
                        id: this.route.idRoute,
                        items: this.routeOperatorService.getOperatorGridEditObjectsFromRoute(route),
                        name: this.ROUTE_OPERATORS_GRID_EDIT_NAME,
                        popupId: this.ROUTE_OPERATORS_GRID_EDIT_POPUP_ID,
                        title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.OperatorsEdit),

                        allowColumnReordering: !this.userSession
                            .getLoggedOperator()
                            .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.RouteAssignedOperators),

                        getAvailableDataSourceMethod: this.userSessionService.getDataProvider().Operator.getOperatorFilter,

                        onSubmit: (added: any[], removed: any[], inGrid: any[]) => {
                            const deferred: ng.IDeferred<boolean> = this.$q.defer<boolean>()

                            this.routeOperatorService
                                .saveOperatorsGridEditResult(this.route, inGrid)
                                .then((savedRoute: OpRoute) => {
                                    this.onRefresh(savedRoute)

                                    deferred.resolve(true)
                                })
                                .fail(() => {
                                    deferred.resolve(false)
                                })

                            return deferred.promise
                        }
                    }
                }, 200)
            })
    }

    private openOperatorDetails(idOperator: number, pageOperatorIds: number[]): void {
        this.routeOperatorService.openOperatorDetails(idOperator, pageOperatorIds)
    }

    //#endregion


    //#region Map

    public mapOptions: MapOptions

    private map: Map
    private isMarkerClicked: boolean
    private rangesVisiblity: boolean

    private clearMap(): void {
        this.mapOptions = null
        this.rangesVisiblity = true
    }

    private clearHiddenMap(): void {
    }

    private createMarkers(tasks: OpTask[]): JQueryPromise<OpMarker[]> {
        return this.routePointService
            .getMarkersFromTasks(tasks)
            .then((markers: OpMarker[]) => {

                _.forEach(markers, (marker: OpMarker) => {
                    marker.onClick = () => {
                        this.isMarkerClicked = true

                        if (this.pointsGrid != null) {
                            this.pointsGrid.selectById([marker.referenceValue])
                        }
                    }
                })

                return markers
            })
    }

    private initializeMap(): void {
        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.RouteMap)) {
            this.mapOptions = null

            this.$timeout(() => {
                MapHelper
                    .getDefaultOptions(this.userSessionService.getDataProvider())
                    .then((mapOptions: MapOptions) => {
                        mapOptions.hideCloseButton = true

                        mapOptions.customButtons = [{
                            class: MapButtonClass.areaVisiblityButton,
                            title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ShowHideRanges),
                            visible: true,

                            onClick: () => {
                                this.toggleRangeVisibility()
                            },
                        }]

                        mapOptions.onMapInit = (map: Map) => {
                            this.map = map

                            this.updateMarkers()
                            this.updateFitterMapAreas()
                        }

                        MapHelper.getLegendItems(this.userSession.getDataProvider(), Enums.ReferenceType.IdLocation)
                            .then((items: MapLegendItem[]) => {
                                mapOptions.legendItems = items
                            })
                            .always(() => {
                                this.mapOptions = mapOptions
                            })
                    })
            }, 100)
        }
    }

    private updateSelectMarkers(): void {
        if (this.pointsGrid != null && this.map != null) {
            this.createMarkers(this.pointsGrid.selectedItems)
                .then((markers: OpMarker[]) => {
                    this.map.setSelectedMarkers(markers, true, true)
                })
        }
    }

    private toggleRangeVisibility(): void {
        this.rangesVisiblity = !this.rangesVisiblity

        this.updateFitterMapAreas()
    }

    private updateMarkers(): void {
        if (this.map != null) {
            this.createMarkers(this.route.taskList)
                .then((markers: OpMarker[]) => {
                    this.map.setMarkers(markers, _.size(markers) > 0)
                })
        }
    }

    //#endregion


    //#region Fitter Operator

    private fitterMapAreas: OpMapArea[]
    private fitterOperator: OpOperator

    private clearFitter(): void {
        this.fitterOperator = null
        this.fitterMapAreas = null
    }

    private initializeFitterOperator(route: OpRoute): void {
        if (this.userSessionService.getLoggedOperator().hasPermission(Activity.ROUTE_SHOW_FITTER_AREA_OP)) {
            if (this.fitterOperator == null || route.idOperatorExecutor !== this.fitterOperator.idOperator) {
                this.routeFitterService
                    .getFitterOperator(route)
                    .then((fitterOperator: OpOperator) => {
                        this.fitterOperator = fitterOperator

                        if (this.fitterOperator != null && this.fitterOperator.area != null) {
                            this.fitterMapAreas = [this.fitterOperator.area]
                        } else {
                            this.fitterMapAreas = []
                        }
                    })
                    .fail((error: any) => {
                        this.fitterMapAreas = []

                        ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                        LogHelper.warn(RouteService.LOG_PREFIX, `Unable to load fitter operator for route with id ${route.idRoute} and fitter id ${route.idOperatorExecutor}.`, error)

                        return $.Deferred().reject(error).promise()
                    })
                    .always(() => {
                        this.updateFitterMapAreas()
                    })
            }
        }
    }

    private updateFitterMapAreas(): void {
        const areas: OpMapArea[] = this.rangesVisiblity ? this.fitterMapAreas : []
        const polygons: OpPolygon[] = []
        const ranges: OpRange[] = []

        _.forEach(areas, (fitterArea: OpMapArea) => {
            switch (fitterArea.type) {
                case Enums.SimaxMapAreaType.Polygon:
                    polygons.push(fitterArea as OpPolygon)
                    break

                case Enums.SimaxMapAreaType.Range:
                    ranges.push(fitterArea as OpRange)

                    break
            }
        })

        if (this.map != null) {
            this.map.setPolygons(polygons)
            this.map.setRanges(ranges)
        }
    }

    //#endregion


    //#region Files

    public trackingOptions: RouteTrackingOptions

    private cleanTracking(): void {
        this.trackingOptions = null
    }

    private initializeTracking(route: OpRoute): void {
        let trackFiles: OpFile[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.RouteTracking)) {
            this.userSessionService
                .getDataProvider().Route
                .getRouteFiles(route.idRoute)
                .then((loadedTrackFiles: OpFile[]) => {
                    trackFiles = loadedTrackFiles
                })
                .fail((error: any) => {
                    ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                    LogHelper.warn(RouteService.LOG_PREFIX, `Unable to load files for route with id ${route.idRoute}.`, error)

                    return $.Deferred().reject(error).promise()
                })
                .always(() => {
                    this.trackingOptions = {
                        tracks: trackFiles
                    }
                })
        }
    }

    //#endregion


    //#region Confirmation Popup

    public confirmationPopupOptions: IMRConfirmationPopupOptions
    private confirmationPopup: IIMRConfirmationPopup

    private initializeConfirmationPopup(): void {
        this.confirmationPopupOptions = {
            onInitialized: (e: IMRConfirmationPopupInitializedArgs) => {
                this.confirmationPopup = e.component
            }
        }
    }

    //#endregion


    //#region Setup

    public getAccordions(): DetailsAccordion[] {
        const accordions: DetailsAccordion[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.RouteParameters)) {
            const items: DetailsAccordionSection[] = []

            items.push({
                name: this.ROUTE_PARAMS_ACCORDION_NAME,
                showInTab: this.ROUTE_COMMON_TAB_NAME,
                showStatus: false,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BasicParameters),
                visible: true,

                canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_EDIT)
                    && !this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.RouteParameters),

                editMethod: () => {
                    this.onEditParams()
                }
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.RouteDetailsAccordions, 0, items))
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.RoutePoints)
            || !this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.RouteAssignedOperators)) {
            const items: DetailsAccordionSection[] = []

            if (!this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.RoutePoints)) {
                items.push({
                    name: this.ROUTE_POINTS_ACCORDION_NAME,
                    showInTab: this.ROUTE_COMMON_TAB_NAME,
                    showStatus: true,
                    title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Points),
                    visible: true,

                    canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_EDIT)
                        && !this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.RoutePoints),

                    editMethod: () => {
                        this.onEditPoints()
                    },

                    status: () => {
                        return `${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Records)}: ${_.size(this.route.taskList)}`
                    },

                    onCollapsed: () => {
                        this.clearHiddenPoints()
                    },

                    onHidden: () => {
                        this.clearHiddenPoints()
                    }
                })
            }

            if (!this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.RouteAssignedOperators)) {
                items.push({
                    name: this.ROUTE_OPERATORS_ACCORDION_NAME,
                    showInTab: this.ROUTE_COMMON_TAB_NAME,
                    showStatus: true,
                    title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Operators),
                    visible: true,

                    canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_EDIT)
                        && !this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.RouteAssignedOperators),

                    editMethod: () => {
                        this.onEditOperators()
                    },

                    status: () => {
                        return `${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Records)}: ${_.size(this.route.operatorList)}`
                    },

                    onCollapsed: () => {
                        this.clearHiddenOperators()
                    },

                    onHidden: () => {
                        this.clearHiddenOperators()
                    }
                })
            }

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.RouteDetailsAccordions, 1, items))
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.RouteMap)) {
            const items: DetailsAccordionSection[] = []

            items.push({
                canEdit: false,
                name: this.ROUTE_MAP_ACCORDION_NAME,
                showInTab: this.ROUTE_COMMON_TAB_NAME,
                showStatus: false,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Map),
                visible: true,

                onCollapsed: () => {
                    this.clearHiddenMap()
                },

                onHidden: () => {
                    this.clearHiddenMap()
                },
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.RouteDetailsAccordions, 2, items))
        }

        return accordions
    }

    public getTabs(): any[] {
        const tabs: any[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.RouteParameters)
            || !this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.RoutePoints)
            || !this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.RouteAssignedOperators)
            || !this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.RouteMap)
        ) {
            tabs.push({
                template: TemplateHelper.getContextMenuItem(
                    LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Common),
                    ConfigHelper.Icon.HOME,
                    this.userSessionService
                ),
                name: this.ROUTE_COMMON_TAB_NAME
            })
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.RouteTracking)) {
            tabs.push({
                text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Tracking),
                icon: "runner",
                name: this.ROUTE_TRACKS_TAB_NAME
            })
        }

        return tabs
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            (event: ng.IAngularEvent, ...args: any[]) => this.onOpen(args[3])
        )

        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            () => this.onClose()
        )

        this.$scope.$on(EventsNames.CLOSE_CREATE_EDIT_POPUP, (event: ng.IAngularEvent, ...args: any[]) => this.onRefresh(args[0]))
    }

    private setOptions(): void {
        this.$scope.popupFor = CommonHelper.route
        this.accordionsNameToShow = this.ROUTE_COMMON_TAB_NAME

        this.$scope.options = <DetailsPopupOptions>{
            closeEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName),
            delHasPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_DEL) ? Activity[Activity.ROUTE_DEL] : null,
            id: this.$scope.id,
            openEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName),
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DETAILS, this.moduleName),

            deleteMethod: () => this.onDelete(),
            getAccordionDataMethod: () => this.getAccordionData(),
            getAccordionSelectionMethod: () => this.getAccordionSelection(),
            updateAccordionMethod: (names: any[]) => this.updateAccordions(names)
        }
    }

    //#endregion

    resetData() { this.onClear() }//DO USUNIECIA!!! - zastąpić onClear
    loadData(id: number, objects: any[]) { this.onOpen(null) }//DO USUNIECIA!!! - zastąpić onOpen
    updateData(result: any) { this.onRefresh(result) }//DO USUNIECIA!!! - zastąpić onRefresh
}